import { useEffect, useState } from 'react';
import { useStrings } from '@/i18n/Localization';
import {
  POLL_MAX_EXPLANATION_BYTES,
  POLL_MAX_OPTIONS,
  POLL_MAX_OPTION_SCALARS,
  POLL_MAX_QUESTION_SCALARS,
  POLL_MIN_OPTIONS,
} from '@/protocol/limits';
import type { PollRecord } from '@/store/types';
import type { Hex } from '@/store/ids';
import dialogStyles from '@/components/DeleteDialog.module.css';
import styles from '@/components/panels.module.css';

/** Counts Unicode scalar values, which is what Section 06 bounds. */
function scalarLength(value: string): number {
  return [...value].length;
}

/** Counts UTF-8 bytes, which is what the explanation limit is expressed in. */
function utf8Length(value: string): number {
  return new TextEncoder().encode(value).length;
}

export interface PollCardProps {
  poll: PollRecord;
  /** Whether the viewer may close the poll (its creator, or an administrator). */
  canClose: boolean;
  onVote: (optionIds: Hex[]) => void;
  onClose: () => void;
}

/**
 * A poll or quiz inside a message.
 *
 * Two Section 06 rules shape everything visible here.
 *
 * "Anonymous results expose counts only." An anonymous poll therefore renders
 * totals and nothing that could identify a voter — and, critically, the client
 * never has voter identities for one, because the server does not send them.
 * The note beside the question says so, so that a participant knows what they
 * are agreeing to before they vote.
 *
 * "Quiz is single-choice with one correct option and explanation ≤1,024 bytes;
 * reveal after vote or closure." The reveal is driven by
 * `poll.correctOption !== null` — the server discloses it to the creator, to a
 * voter who has voted, or after closure, and withholds it otherwise. The client
 * does not decide when to reveal; it decides only how to present what it has
 * been given. That distinction matters: a client that held the answer and hid
 * it would leak it to anyone who opened the developer tools.
 */
export function PollCard(props: PollCardProps) {
  const { t } = useStrings();
  const { poll } = props;
  const [pending, setPending] = useState<Set<string>>(new Set(poll.myVotes));

  useEffect(() => {
    setPending(new Set(poll.myVotes));
  }, [poll.myVotes]);

  const hasVoted = poll.myVotes.length > 0;
  const isQuiz = poll.correctOption !== null || poll.explanation !== null;
  const revealed = poll.correctOption !== null;
  // A quiz answer cannot be changed once given; an ordinary poll can be, unless
  // it is closed.
  const canChange = !poll.closed && (!isQuiz || !hasVoted);
  const totalVotes = poll.options.reduce((sum, option) => sum + option.votes, 0);

  const choose = (optionId: Hex) => {
    if (!canChange) {
      return;
    }
    if (!poll.multiple) {
      props.onVote([optionId]);
      return;
    }
    const next = new Set(pending);
    if (next.has(optionId)) {
      next.delete(optionId);
    } else {
      next.add(optionId);
    }
    setPending(next);
  };

  return (
    <div className={styles.poll}>
      <p className={styles.pollQuestion} dir="auto">
        {poll.question}
      </p>
      <p className={styles.pollNote}>
        {poll.anonymous ? `${t('poll.anonymousNote')} ` : ''}
        {poll.multiple ? t('poll.multipleNote') : t('poll.singleNote')}
        {poll.closed ? ` · ${t('poll.closed')}` : ''}
      </p>
      {isQuiz && !revealed ? <p className={styles.pollNote}>{t('poll.quizHidden')}</p> : null}

      {poll.options.map((option, index) => {
        const chosen = pending.has(option.id);
        const correct = revealed && poll.correctOption === option.id;
        const share = totalVotes > 0 ? Math.round((option.votes / totalVotes) * 100) : 0;
        return (
          <button
            key={option.id}
            type="button"
            className={`${styles.pollOption} ${chosen ? styles.pollOptionChosen : ''} ${
              correct ? styles.pollOptionCorrect : ''
            }`}
            aria-pressed={poll.multiple ? chosen : undefined}
            disabled={!canChange}
            onClick={() => {
              choose(option.id);
            }}
          >
            <span dir="auto">
              {option.text}
              {/*
                The correct answer and the viewer's own answer are named in
                text, not signalled only by a border colour.
              */}
              {correct ? ` — ${t('poll.quizCorrect')}` : ''}
              {chosen && hasVoted ? ` — ${t('poll.quizYourAnswer')}` : ''}
            </span>
            <span className={styles.rowMeta}>{t('poll.votes', { count: option.votes })}</span>
            <progress
              className={styles.pollBar}
              max={100}
              value={share}
              aria-label={t('poll.votes', { count: option.votes })}
            />
          </button>
        );
      })}

      {isQuiz && revealed && hasVoted ? (
        <p className={styles.pollNote}>
          {poll.myVotes[0] === poll.correctOption ? t('poll.quizRight') : t('poll.quizWrong')}
          {poll.explanation === null ? '' : ` ${poll.explanation}`}
        </p>
      ) : null}

      <div className={styles.buttonRow}>
        {poll.multiple && canChange ? (
          <button
            type="button"
            className={`${styles.button} ${styles.buttonPrimary}`}
            disabled={pending.size === 0}
            onClick={() => {
              props.onVote([...pending] as Hex[]);
            }}
          >
            {t('poll.vote')}
          </button>
        ) : null}
        {props.canClose && !poll.closed ? (
          <button type="button" className={styles.button} onClick={props.onClose}>
            {t('poll.close')}
          </button>
        ) : null}
      </div>
    </div>
  );
}

export interface CreatePollDialogProps {
  onCreate: (input: {
    question: string;
    options: string[];
    multiple: boolean;
    anonymous: boolean;
    correctIndex: number | null;
    explanation: string | null;
    closesAt: number | null;
  }) => void;
  onClose: () => void;
}

/**
 * Composing a poll or a quiz.
 *
 * The Section 06 bounds — 2–10 options, a question of at most 300 Unicode
 * scalar values, options of at most 100, an explanation of at most 1,024 bytes
 * — are enforced in the form rather than only on the server, so the user finds
 * out while they are still typing rather than after pressing create.
 *
 * Quiz mode forces single choice: Section 06 defines a quiz as single-choice
 * with one correct option, so the two controls are not independent and the form
 * does not pretend otherwise.
 */
export function CreatePollDialog(props: CreatePollDialogProps) {
  const { t } = useStrings();
  const [question, setQuestion] = useState('');
  const [options, setOptions] = useState<string[]>(['', '']);
  const [multiple, setMultiple] = useState(false);
  const [anonymous, setAnonymous] = useState(false);
  const [quiz, setQuiz] = useState(false);
  const [correctIndex, setCorrectIndex] = useState(0);
  const [explanation, setExplanation] = useState('');
  const [closesInHours, setClosesInHours] = useState('');

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        props.onClose();
      }
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
    };
  }, [props]);

  const filled = options.map((option) => option.trim()).filter((option) => option.length > 0);
  const valid =
    question.trim().length > 0 &&
    scalarLength(question) <= POLL_MAX_QUESTION_SCALARS &&
    filled.length >= POLL_MIN_OPTIONS &&
    filled.every((option) => scalarLength(option) <= POLL_MAX_OPTION_SCALARS) &&
    utf8Length(explanation) <= POLL_MAX_EXPLANATION_BYTES;

  const submit = () => {
    if (!valid) {
      return;
    }
    const hours = Number.parseInt(closesInHours, 10);
    props.onCreate({
      question: question.trim(),
      options: filled,
      // Quiz mode is single-choice by definition.
      multiple: quiz ? false : multiple,
      anonymous,
      correctIndex: quiz ? Math.min(correctIndex, filled.length - 1) : null,
      explanation: quiz && explanation.trim().length > 0 ? explanation.trim() : null,
      closesAt: Number.isFinite(hours) && hours > 0 ? Date.now() + hours * 3_600_000 : null,
    });
  };

  return (
    <div className={dialogStyles.backdrop}>
      <div
        className={dialogStyles.dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby="poll-title"
      >
        <h2 id="poll-title" className={dialogStyles.title}>
          {t('poll.createTitle')}
        </h2>

        <div className={styles.field}>
          <label htmlFor="poll-question">{t('poll.question')}</label>
          <input
            id="poll-question"
            className={styles.input}
            dir="auto"
            value={question}
            maxLength={POLL_MAX_QUESTION_SCALARS * 2}
            onChange={(event) => {
              setQuestion(event.target.value);
            }}
          />
        </div>

        {options.map((option, index) => (
          <div className={styles.field} key={index}>
            <label htmlFor={`poll-option-${String(index)}`}>
              {t('poll.option', { number: index + 1 })}
            </label>
            <input
              id={`poll-option-${String(index)}`}
              className={styles.input}
              dir="auto"
              value={option}
              onChange={(event) => {
                const next = [...options];
                next[index] = event.target.value;
                setOptions(next);
              }}
            />
            {quiz ? (
              <label className={styles.checkboxRow}>
                <input
                  type="radio"
                  name="poll-correct"
                  checked={correctIndex === index}
                  onChange={() => {
                    setCorrectIndex(index);
                  }}
                />
                {t('poll.correct', { number: index + 1 })}
              </label>
            ) : null}
            {options.length > POLL_MIN_OPTIONS ? (
              <button
                type="button"
                className={`${styles.action} ${styles.destructive}`}
                onClick={() => {
                  setOptions(options.filter((_, position) => position !== index));
                  if (correctIndex >= options.length - 1) {
                    setCorrectIndex(0);
                  }
                }}
              >
                {t('poll.removeOption', { number: index + 1 })}
              </button>
            ) : null}
          </div>
        ))}

        {options.length < POLL_MAX_OPTIONS ? (
          <button
            type="button"
            className={styles.action}
            onClick={() => {
              setOptions([...options, '']);
            }}
          >
            {t('poll.addOption')}
          </button>
        ) : null}

        <label className={styles.checkboxRow}>
          <input
            type="checkbox"
            checked={quiz}
            onChange={(event) => {
              setQuiz(event.target.checked);
            }}
          />
          {t('poll.quiz')}
        </label>
        {quiz ? <p className={styles.hint}>{t('poll.quizHint')}</p> : null}

        {quiz ? null : (
          <label className={styles.checkboxRow}>
            <input
              type="checkbox"
              checked={multiple}
              onChange={(event) => {
                setMultiple(event.target.checked);
              }}
            />
            {t('poll.multiple')}
          </label>
        )}

        <label className={styles.checkboxRow}>
          <input
            type="checkbox"
            checked={anonymous}
            onChange={(event) => {
              setAnonymous(event.target.checked);
            }}
          />
          {t('poll.anonymous')}
        </label>

        {quiz ? (
          <div className={styles.field}>
            <label htmlFor="poll-explanation">{t('poll.explanation')}</label>
            <input
              id="poll-explanation"
              className={styles.input}
              dir="auto"
              value={explanation}
              onChange={(event) => {
                setExplanation(event.target.value);
              }}
            />
          </div>
        ) : null}

        <div className={styles.field}>
          <label htmlFor="poll-closes">{t('poll.closesAt')}</label>
          <input
            id="poll-closes"
            className={styles.input}
            type="number"
            min={0}
            value={closesInHours}
            onChange={(event) => {
              setClosesInHours(event.target.value);
            }}
          />
        </div>

        <div className={dialogStyles.actions}>
          <button type="button" className={dialogStyles.cancel} onClick={props.onClose}>
            {t('group.cancel')}
          </button>
          <button
            type="button"
            className={dialogStyles.confirm}
            disabled={!valid}
            onClick={submit}
          >
            {t('poll.createAction')}
          </button>
        </div>
      </div>
    </div>
  );
}
